// 默认请求头
import { _get, _getNull, isFunction, isObject, showLoading, showToast, showToastAsync, } from "@/utils/utils.js";
import { httpLog as log, httpErrorLog as errorLog } from '@/utils/log'
import { storage } from "@/utils/storage";

/**
 * 获取真正请求的 url 地址
 * @param baseOptions
 * @param options
 * @returns {`${string}${*}`}
 */
const getRequestUrl = (baseOptions = {}, options = {}) => {
    const prefixUrl = _get(baseOptions, 'prefixUrl', '');
    const url = _get(options, 'url', '');
    if (!url) {
        throw new Error('接口地址填写异常');
    }
    return `${prefixUrl}${url}`
}

/**
 * 再运行时获取真实数据
 * @param obj
 * @returns {{}}
 */
const getRealDataByRunning = (obj) => {
    let data = {};
    Object.keys(obj).forEach(k => {
        if (isFunction(obj[k])) {
            try {
                data[k] = obj[k]();
            } catch (e) {
                console.error(`---------接口请求初始化字段${k}异常:`, e);
            }
            return
        }
        data[k] = obj[k];
    })
    return data;
}

/**
 * 获取数据通过配置
 * @param configArray
 * @param key
 * @returns {*[]}
 */
const getDataByConfig = (configArray, key) => {
    let dataArray = [];
    configArray.forEach(c => {
        const _c = _get(c, key, {});
        dataArray.push(isObject(_c) ? getRealDataByRunning(_c) : {});
    })
    return dataArray
}


/**
 * 获取请求头
 * @param baseOptions
 * @param options
 * @returns {{"Content-Type": string}}
 */
const getRequestHeaders = (baseOptions = {}, options = {}) => {
    const [baseHeadersData, headersData] = getDataByConfig([baseOptions, options], 'headers')
    return {
        // 请求头默认 application/json
        'Content-Type': 'application/json',
        ...baseHeadersData,
        ...headersData,
    }
}

/**
 * 获取请求接口的入参
 * @param baseOptions
 * @param options
 * @returns {{}}
 */
const getRequestData = (baseOptions = {}, options = {}) => {
    const [baseParamsData, paramsData] = getDataByConfig([baseOptions, options], 'params')
    return {
        ...baseParamsData,
        ...paramsData,
    }
}

/**
 * 获取请求方式 get post
 * @param baseOptions
 * @param options
 * @returns {*|string}
 */
const getRequestMethod = (baseOptions = {}, options = {}) => {
    const baseMethod = _get(baseOptions, 'method', '');
    const method = _get(options, 'method', '');
    // 默认 post 请求
    return method || baseMethod || 'POST';
}

/**
 * 获取请求前置拦截回调方法
 * @param baseOptions
 * @param options
 * @returns {*}
 */
const getRequestBeforeRequest = (baseOptions = {}, options = {}) => {
    const baseBeforeRequest = _get(baseOptions, 'beforeRequest', '');
    const beforeRequest = _get(options, 'beforeRequest', '');
    return beforeRequest || baseBeforeRequest
}

const getRequestTimeout = (baseOptions = {}, options = {}) => {
    const baseTimeout = _get(baseOptions, 'timeout', 5000);
    const timeout = _get(options, 'timeout');
    return timeout || baseTimeout
}

const cachePreFix = `__http_cache_`;

/**
 * 获取接口缓存key
 * @param {*} url
 * @param {*} data
 * @returns
 */
const getCacheKeyByUrlData = (url, data) => {
    return `${cachePreFix}${url}_${JSON.stringify(data)}`
}

/**
 * 查询http目前的缓存数量
 * @returns
 */
export const getCacheCount = async () => {
    return new Promise((resolve) => {
        let count = 0;
        uni.getStorageInfo({
            success(res) {
                res.keys.forEach(key => {
                    key.startsWith(cachePreFix) && count++;
                });
                resolve(count);
            },
            fail() {
                resolve(0);
            }
        })
    })
}


/**
 * 清空http所有接口缓存数据
 * @returns
 */
export const removeAllHttpCache = async () => {
    return new Promise((resolve) => {
        uni.getStorageInfo({
            success(res) {
                res.keys.forEach(key => {
                    key.startsWith(cachePreFix) && storage.remove(key);
                });
                resolve(true);
            },
            fail() {
                resolve(null);
            }
        })
    })
}

/**
 * 获取请求失败后,重复请求次数,默认1次
 * @param {*} baseOptions
 * @param {*} options
 * @returns
 */
const getRepeat = (baseOptions = {}, options = {}) => {
    const baseRepeat = _get(baseOptions, 'repeat', 0);
    return _get(options, 'repeat', baseRepeat);
}

const request = async (baseOptions, options, { resolve, reject }) => {
    // request success
    // 全局 success
    const baseSuccess = _get(baseOptions, 'success', null);
    // 当前接口 success
    const success = _get(options, 'success', null);
    // request fail
    // 全局 fail
    const baseFail = _get(baseOptions, 'fail', null);
    // 当前接口 fail
    const fail = _get(options, 'fail', null);
    // 请求前置拦截
    const beforeRequest = getRequestBeforeRequest(baseOptions, options);
    // 请求地址
    const url = getRequestUrl(baseOptions, options);
    // 请求入参
    const data = getRequestData(baseOptions, options);
    // 请求头
    const headers = getRequestHeaders(baseOptions, options);
    // 请求方法
    const method = getRequestMethod(baseOptions, options);

    const hideLoading = _getNull(options, 'meta.loading') ? showLoading(_get(options, 'meta.loadingText', '加载中')) : null;

    const getFailF = (f) => {
        return (...argus) => {
            let repeat = getRepeat(baseOptions, options);
            if (repeat > 0) {
                options.repeat = repeat - 1;
                return request(baseOptions, options, { resolve, reject });
            }
            f(...argus);
        }
    }

    const successF = (res) => {
        hideLoading && hideLoading();
        const baseRes = baseSuccess ? baseSuccess(res, { url, data, requestConfig, headers, options }) : res;
        resolve(success ? success(baseRes, { url, data, requestConfig, headers, options }) : baseRes);
    }

    const failF = getFailF(async (res) => {
        if (_getNull(options, 'meta.toast')) {
            await showToastAsync(_get(res, 'message', _get(options, 'meta.toastText', '网络繁忙，请稍后重试~')))
        }
        const baseRes = baseFail ? baseFail(res, { url, data, requestConfig, headers, options }) : res;
        resolve(fail ? fail(baseRes, { url, data, requestConfig, headers, options }) : baseRes);
    })

    const _requestStart = +new Date();

    // 请求前拦截
    const requestConfig = beforeRequest
        ? beforeRequest({ headers, data, url, method, _requestStart })
        : { headers, data, url, method, _requestStart };

    // 处理mock数据
    if (options.mockData) {
        try {
            options.mockStatus === 'fail' ? failF(options.mockData) : successF(options.mockData);
            return;
        } catch (e) {
            errorLog('http请求mock异常', e);
        }
    }

    if (!requestConfig) {
        hideLoading && hideLoading();
        return failF({});
    }

    // 判断当前接口需要走缓存逻辑
    const cacheInfo = options.cache || null;
    // 配置项有缓存
    if (cacheInfo) {
        // 查询当前接口和入参是否有对应缓存
        let httpCache = storage.get(getCacheKeyByUrlData(url, data));
        if (httpCache) {
            // 有对应缓存,校验是否使用当前缓存
            if (cacheInfo.useCache({ data, headers })) {
                successF(httpCache);
                return;
            }
            // 不使用当前缓存,需删除缓存
            storage.remove(getCacheKeyByUrlData(url, data));
        }
    }
    uni.request({
        url: requestConfig.url,
        header: requestConfig.headers,
        data: requestConfig.data,
        timeout: getRequestTimeout(baseOptions, options),
        // 请求方式
        method: requestConfig.method,
        // 请求成功
        success(res) {
            try {
                if (!baseOptions.customLog) {
                    log('\n', `${requestConfig.url} --- 接口信息：\n`,
                        '入参：', data, '\n',
                        "耗时：", +new Date() - requestConfig._requestStart, '\n', '\n',
                        "header：", requestConfig.headers, '\n',
                        "返回：", res, '\n', '\n',
                    );
                }
            } catch (e) {

            }
            if (res.statusCode === 200) {
                // 如果需要缓存,根据配置的时间缓存
                cacheInfo && storage.set(getCacheKeyByUrlData(url, data), res.data, { duration: (cacheInfo.duration || 1000 * 60 * 60 * 2) / 1000 });
                successF(res.data);
                return;
            }
            hideLoading && hideLoading();
            failF(res.data);
        },
        // 请求失败
        fail(err) {
            try {
                errorLog('\n', `${requestConfig.url} --- 接口信息：\n`,
                    '入参：', {
                        requestConfigData: requestConfig.data,
                        data,
                    }, '\n',
                    "耗时：", +new Date() - requestConfig._requestStart, '\n', '\n',
                    "header：", requestConfig.headers, '\n',
                    "返回：", err, '\n', '\n',
                );
            } catch (e) {

            }
            hideLoading && hideLoading();
            failF(err);
        }
    })
}

/**
 * 基础 http 请求
 * @param baseOptions
 * @returns {function(*): Promise<unknown>}
 * @constructor
 */
export const Http = (baseOptions = {}) => {
    return (options = {}) => {
        return new Promise((resolve, reject) => {
            request(baseOptions, options, { resolve, reject });
        })
    }
}
